import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Table,
  ArrowUpRightIc,
  Dialog,
  Button,
  DialogFooter,
} from "@/components/Elements";
import { RootState } from "@/store/reducers";

import {
  useBlockUnblockAccountMutation,
  useConfirmAccountMutation,
  useGetAccountListMutation,
} from "../api/account";
import { mapStringToLocation, useLocation } from "@/hooks/useLocation";
import { ProfileModel } from "../types";
import useToggle from "@/hooks/useToggle";
import { AccountDetail } from "./AccountDetail";
import { themeTextColor } from "@/utils/colorConstant";
import { InputField } from "@/components/Form";
import { useDebounce } from "@/hooks";
import { contains } from "@/utils/helper";

const Action = ({ entry }: { entry: ProfileModel }) => {
  const { show: showModal, toggle: toggleModal } = useToggle();
  const { show: showConfirmModal, toggle: toggleConfirmModal } = useToggle();
  const { show: showConfirmEmailModal, toggle: toggleConfirmEmailModal } =
    useToggle();
  const blockTxt = entry.isActive ? "Block" : "Unblock";
  const [blockUnblockAccount] = useBlockUnblockAccountMutation();
  const [confirmAccount] = useConfirmAccountMutation();

  const handleBlockUnblock = () => {
    try {
      blockUnblockAccount({
        userId: entry.userId,
        isActive: !entry.isActive,
      });
    } catch (ex) {
      console.log(`ex`, ex);
    }
    toggleConfirmModal();
  };

  const handleConfirmAccount = () => {
    try {
      confirmAccount(entry.userId);
    } catch (ex) {
      console.log(`ex`, ex);
    }
    toggleConfirmEmailModal();
  };

  return (
    <>
      <div className="flex justify-between">
        <button onClick={toggleModal}>
          <ArrowUpRightIc className={`w-6 h-6 ${themeTextColor}`} />
        </button>
        <Button size="sm" className="ml-2" onClick={toggleConfirmModal}>
          {blockTxt}
        </Button>
        {!entry.isEmailVerified && (
          <Button size="sm" className="ml-2" onClick={toggleConfirmEmailModal}>
            Verify Email
          </Button>
        )}
      </div>
      <Dialog isOpen={showModal} onClose={toggleModal} title="Profile Detail">
        <AccountDetail userDetail={entry} />
      </Dialog>
      <Dialog
        isOpen={showConfirmModal}
        onClose={toggleConfirmModal}
        title={`${blockTxt} Account`}
      >
        You are about to {blockTxt} this account. Please click Yes to proceed.
        <DialogFooter onClose={toggleConfirmModal}>
          <Button size="sm" className="ml-2" onClick={handleBlockUnblock}>
            Yes
          </Button>
        </DialogFooter>
      </Dialog>
      <Dialog
        isOpen={showConfirmEmailModal}
        onClose={toggleConfirmEmailModal}
        title={`Verify Email`}
      >
        You are about to verify email of this account. Please click Yes to
        proceed.
        <DialogFooter onClose={toggleConfirmEmailModal}>
          <Button size="sm" className="ml-2" onClick={handleConfirmAccount}>
            Yes
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export const AccountList = () => {
  const [getAccountList] = useGetAccountListMutation();
  const allProfileList = useSelector(
    (state: RootState) => state.account.profileList
  );
  const [profileList, setProfileList] = useState<ProfileModel[]>([]);
  const [searchBy, setSearchBy] = useState<string>("");
  const { getAddressDetail } = useLocation();
  const debounceFn = useDebounce();

  useEffect(() => {
    (async () => {
      setProfileList(
        await Promise.all(
          allProfileList.map(async (c) => {
            const location = mapStringToLocation(c.location);
            return {
              ...c,
              address: location
                ? (await getAddressDetail(location))?.address
                : undefined,
            };
          })
        )
      );
    })();
  }, [allProfileList]);

  const filteredProfileList = useMemo(() => {
    if (searchBy) {
      return profileList.filter(
        (c) =>
          contains(c.firstName, searchBy) ||
          contains(c.lastName, searchBy) ||
          contains(c.role, searchBy) ||
          contains(c.email, searchBy) ||
          contains(c.address, searchBy)
      );
    }
    return profileList;
  }, [profileList, searchBy]);

  useEffect(() => {
    getAccountList();
  }, []);

  return (
    <div>
      <div>
        <InputField
          name="searchBy"
          placeholder="Search by..."
          registration={{
            onChange: async ({ target }) => setSearchBy(target.value),
          }}
          value={searchBy}
        />
      </div>
      <Table
        data={filteredProfileList}
        id="userId"
        columns={[
          {
            title: "First Name",
            field: "firstName",
          },
          {
            title: "Last Name",
            field: "lastName",
          },
          {
            title: "Role",
            field: "role",
          },
          {
            title: "Email",
            field: "email",
          },
          {
            title: "Phone",
            field: "mobile",
          },
          {
            title: "Distance Radius (miles)",
            field: "distanceRadius",
          },
          {
            title: "Address",
            field: "address",
          },
          {
            title: "View",
            field: "action",
            Cell: Action,
          },
        ]}
      />
    </div>
  );
};
